import { Component, createRef } from 'preact';
import { GOLD, ACCENT_GOLD } from '../lib/medievalTheme';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const CELL_HEIGHT = 13;
const STACK_PADDING_Y = 6;
const MAX_BOOST = 1.0;
const SIGMA = 5;

/**
 * Vertical A–Z index that sits next to the card grid scroll bar.
 *
 * Hover or drag a letter to scroll the grid to its first card. The
 * column animates with a macOS-Dock-style fish-eye:
 *
 *   - Each letter's slot height = base * scale, so neighbors physically
 *     spread apart as the focal cell grows. This is what prevents
 *     overlap and gives the smooth arch shape.
 *   - Letters scale via the same Gaussian curve, anchored to the right
 *     edge so the focal letter pops out leftward toward the cards.
 *   - The whole stack re-centers as it grows, so the column stays
 *     visually balanced regardless of which letter is focused.
 */
export default class AlphabetScrollIndex extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hoveredLetter: null,
      isDragging: false,
      containerHeight: 0,
    };
    this.containerRef = createRef();
    this.letterRefs = ALPHABET.map(() => createRef());
    this._resizeObs = null;
  }

  componentDidMount() {
    this.measureContainer();
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(() => this.measureContainer());
      if (this.containerRef.current) this._resizeObs.observe(this.containerRef.current);
    }
  }

  componentWillUnmount() {
    if (this._resizeObs) {
      this._resizeObs.disconnect();
      this._resizeObs = null;
    }
  }

  measureContainer = () => {
    const el = this.containerRef.current;
    if (!el) return;
    const containerHeight = el.clientHeight;
    if (containerHeight !== this.state.containerHeight) {
      this.setState({ containerHeight });
    }
  };

  findNearestExisting(letter, letterMap) {
    const targetIdx = ALPHABET.indexOf(letter);
    if (targetIdx === -1) return null;
    for (let i = targetIdx; i < ALPHABET.length; i += 1) {
      if (letterMap.has(ALPHABET[i])) return ALPHABET[i];
    }
    for (let i = targetIdx - 1; i >= 0; i -= 1) {
      if (letterMap.has(ALPHABET[i])) return ALPHABET[i];
    }
    return null;
  }

  letterAtPoint(clientY) {
    const refs = this.letterRefs;
    const firstEl = refs[0].current;
    const lastEl = refs[refs.length - 1].current;
    if (firstEl && clientY < firstEl.getBoundingClientRect().top) return ALPHABET[0];
    if (lastEl && clientY >= lastEl.getBoundingClientRect().bottom) return ALPHABET[refs.length - 1];
    for (let i = 0; i < refs.length; i += 1) {
      const el = refs[i].current;
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY < r.bottom) return ALPHABET[i];
    }
    return null;
  }

  scrollToLetter(letter) {
    const { letterMap, onJumpToIndex } = this.props;
    const target = this.findNearestExisting(letter, letterMap);
    if (!target) return;
    const idx = letterMap.get(target);
    if (typeof idx === 'number') onJumpToIndex?.(idx);
  }

  handlePointerDown = (e) => {
    e.preventDefault();
    const letter = this.letterAtPoint(e.clientY);
    if (!letter) return;
    this.setState({ isDragging: true, hoveredLetter: letter });
    this.scrollToLetter(letter);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  handlePointerMove = (e) => {
    const letter = this.letterAtPoint(e.clientY);
    if (!letter) return;
    if (this.state.isDragging) {
      if (letter !== this.state.hoveredLetter) {
        this.setState({ hoveredLetter: letter });
        this.scrollToLetter(letter);
      }
    } else if (letter !== this.state.hoveredLetter) {
      this.setState({ hoveredLetter: letter });
    }
  };

  handlePointerUp = (e) => {
    this.setState({ isDragging: false });
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  handlePointerLeave = () => {
    if (!this.state.isDragging) {
      this.setState({ hoveredLetter: null });
    }
  };

  render() {
    const { letterMap, activeLetter } = this.props;
    const { hoveredLetter, isDragging, containerHeight } = this.state;
    const focusIdx = hoveredLetter ? ALPHABET.indexOf(hoveredLetter) : -1;
    const badgeLetter = hoveredLetter
      ? this.findNearestExisting(hoveredLetter, letterMap || new Map())
      : null;

    // Per-letter cell heights driven by Gaussian fish-eye. Cells
    // expand alongside their letter's scale so neighbors slide apart
    // and never overlap. The macOS Dock signature behaviour.
    const cellHeights = ALPHABET.map((_, i) => {
      if (focusIdx < 0) return CELL_HEIGHT;
      const dist = Math.abs(i - focusIdx);
      const boost = MAX_BOOST * Math.exp(-(dist * dist) / SIGMA);
      return CELL_HEIGHT * (1 + boost);
    });

    const cellTops = [];
    let acc = 0;
    for (let i = 0; i < cellHeights.length; i += 1) {
      cellTops.push(acc);
      acc += cellHeights[i];
    }
    const totalStackHeight = acc + STACK_PADDING_Y * 2;
    const stackTopOffset = containerHeight > 0
      ? Math.max(0, (containerHeight - totalStackHeight) / 2)
      : 0;

    const badgeTop = focusIdx >= 0
      ? stackTopOffset + STACK_PADDING_Y + cellTops[focusIdx] + cellHeights[focusIdx] / 2
      : stackTopOffset + totalStackHeight / 2;

    return (
      <div
        ref={this.containerRef}
        onPointerDown={this.handlePointerDown}
        onPointerMove={this.handlePointerMove}
        onPointerUp={this.handlePointerUp}
        onPointerCancel={this.handlePointerUp}
        onPointerLeave={this.handlePointerLeave}
        className={`relative select-none touch-none shrink-0 ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
        style={{ width: '1.875cqw' }}
      >
        <div
          style={{
            position: 'absolute',
            top: stackTopOffset,
            left: 0,
            right: 0,
            padding: `${STACK_PADDING_Y}px 0`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            transition: 'top 0.1s ease-out',
          }}
        >
          {ALPHABET.map((letter, i) => {
            const exists = letterMap?.has(letter);
            const isActive = letter === activeLetter;
            const dist = focusIdx >= 0 ? Math.abs(i - focusIdx) : Infinity;
            const boost = focusIdx >= 0 ? MAX_BOOST * Math.exp(-(dist * dist) / SIGMA) : 0;
            const scale = 1 + boost;

            let opacity = exists ? 0.55 : 0.14;
            let color = exists ? '#8a7041' : '#4a3d28';
            let weight = 600;

            if (focusIdx >= 0) {
              if (dist === 0) {
                opacity = 1;
                color = exists ? '#f4e4b8' : color;
                weight = 700;
              } else {
                const t = Math.max(0, 1 - dist / 6);
                opacity = exists ? 0.55 + 0.45 * t : 0.14 + 0.22 * t;
                if (dist <= 2 && exists) color = '#d7c896';
              }
            } else if (isActive) {
              color = ACCENT_GOLD;
              opacity = 1;
            }

            return (
              <span
                key={letter}
                ref={this.letterRefs[i]}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 5,
                  width: '100%',
                  height: cellHeights[i],
                  fontSize: '0.5625cqw',
                  fontWeight: weight,
                  letterSpacing: '0.4px',
                  lineHeight: 1,
                  color,
                  opacity,
                  transform: `scale(${scale})`,
                  transformOrigin: 'right center',
                  transition: isDragging
                    ? 'transform 0.06s linear, height 0.06s linear, color 0.08s, opacity 0.08s'
                    : 'transform 0.1s ease-out, height 0.1s ease-out, color 0.12s, opacity 0.12s',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  willChange: 'transform, height',
                }}
              >
                {letter}
              </span>
            );
          })}
        </div>

        {/* Floating focus badge. Appears left of the column while interacting */}
        <div
          style={{
            position: 'absolute',
            right: '100%',
            top: badgeTop,
            marginRight: 8,
            transform: `translateY(-50%) scale(${hoveredLetter ? 1 : 0.85})`,
            transformOrigin: 'right center',
            opacity: hoveredLetter ? 1 : 0,
            transition: 'opacity 0.16s ease, transform 0.16s ease, top 0.08s ease-out',
            pointerEvents: 'none',
            width: '2.125cqw',
            height: '2.125cqw',
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(18,14,10,0.92)',
            border: `1px solid ${GOLD} 0.55)`,
            boxShadow: `0 0 14px ${GOLD} 0.35), inset 0 1px 0 rgba(255,255,255,0.05)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: ACCENT_GOLD,
            fontSize: '1.125cqw',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textShadow: `0 0 8px ${GOLD} 0.55)`,
          }}
        >
          {badgeLetter || hoveredLetter || ''}
        </div>
      </div>
    );
  }
}
